import React from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { LoadingState, BarGraph, ReportTable } from './ReportHelpers';

export function KPIsTab({ data, loading, onExport }) {
  if (loading) return <LoadingState />;
  if (!data) return null;
  const handleExport = () => {
    const headers = ['Name', 'Type', 'Quotes Created', 'Quotes Converted', 'Conversion Rate %', 'Assigned Jobs', 'Completed Jobs', 'Completion Rate %', 'Invoices Created', 'Invoice Value'];
    const rows = data.items.map(r => [r.name, r.user_type, r.quotes_created, r.quotes_converted, r.conversion_rate, r.assigned_jobs, r.completed_jobs, r.completion_rate, r.invoices_created, r.invoice_value]);
    const sum = (key) => data.items.reduce((s, r) => s + (r[key] || 0), 0);
    const tQuotes = sum('quotes_created'), tConverted = sum('quotes_converted');
    const tAssigned = sum('assigned_jobs'), tCompleted = sum('completed_jobs');
    const summaryRow = [
      'TOTAL', '',
      tQuotes, tConverted,
      tQuotes ? Math.round((tConverted / tQuotes) * 100) : 0,
      tAssigned, tCompleted,
      tAssigned ? Math.round((tCompleted / tAssigned) * 100) : 0,
      sum('invoices_created'), sum('invoice_value'),
    ];
    onExport('KPI Report', headers, rows, summaryRow);
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(num || 0);

  const chartData = data.items.filter(r => r.assigned_jobs > 0 || r.quotes_created > 0).map(r => ({ name: r.name, value: r.completion_rate }));
  const conversionChart = data.items.filter(r => r.quotes_created > 0).map(r => ({ name: r.name, value: r.conversion_rate }));

  return (
    <div className="space-y-4" data-testid="kpis-tab">
      <div className="flex justify-end print:hidden">
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700" data-testid="export-kpis-excel">
          <FileSpreadsheet size={14} /> Export Excel
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border rounded p-3">
          <p className="text-xs text-slate-500 uppercase">Total Staff</p>
          <p className="text-xl font-bold text-slate-800" data-testid="kpi-total-staff">{data.count || 0}</p>
        </div>
        <div className="bg-white border rounded p-3">
          <p className="text-xs text-slate-500 uppercase">Total Quotes Created</p>
          <p className="text-xl font-bold text-slate-800" data-testid="kpi-total-quotes">{data.items.reduce((s, r) => s + (r.quotes_created || 0), 0)}</p>
        </div>
        <div className="bg-white border rounded p-3">
          <p className="text-xs text-slate-500 uppercase">Total Jobs Assigned</p>
          <p className="text-xl font-bold text-slate-800" data-testid="kpi-total-jobs">{data.items.reduce((s, r) => s + (r.assigned_jobs || 0), 0)}</p>
        </div>
        <div className="bg-white border rounded p-3">
          <p className="text-xs text-slate-500 uppercase">Total Invoiced</p>
          <p className="text-xl font-bold text-slate-800" data-testid="kpi-total-invoiced">{formatCurrency(data.items.reduce((s, r) => s + (r.invoice_value || 0), 0))}</p>
        </div>
      </div>

      {chartData.length > 0 && <BarGraph data={chartData} title="Job Completion Rate by Staff (%)" />}
      {conversionChart.length > 0 && <BarGraph data={conversionChart} title="Quote Conversion Rate by Staff (%)" />}
      <ReportTable
        headers={['Name', 'Type', 'Quotes', 'Converted', 'Conv. %', 'Jobs Assigned', 'Jobs Done', 'Comp. %', 'Invoices', 'Invoice Value']}
        rows={data.items.map(r => [r.name, r.user_type, r.quotes_created, r.quotes_converted, `${r.conversion_rate}%`, r.assigned_jobs, r.completed_jobs, `${r.completion_rate}%`, r.invoices_created, formatCurrency(r.invoice_value)])}
        footer={(() => {
          const t = (k) => data.items.reduce((s, r) => s + (r[k] || 0), 0);
          const tQ = t('quotes_created'), tC = t('quotes_converted');
          const tA = t('assigned_jobs'), tD = t('completed_jobs');
          const conv = tQ ? Math.round((tC / tQ) * 1000) / 10 : 0;
          const comp = tA ? Math.round((tD / tA) * 1000) / 10 : 0;
          return ['TOTAL', '', tQ, tC, `${conv}%`, tA, tD, `${comp}%`, t('invoices_created'), formatCurrency(t('invoice_value'))];
        })()}
        testId="kpis-table"
      />
    </div>
  );
}
